import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QrCode, CreditCard, Loader2, Tag, CheckCircle2 } from "lucide-react";
import { PixPayment } from "./PixPayment";
import { CreditCardPayment } from "./CreditCardPayment";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { normalizeCouponCode } from "@/services/couponService";
import { cn } from "@/lib/utils";

interface PaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planId?: string;
  onSuccess?: () => void;
}

export function PaymentModal({ open, onOpenChange, planId = 'plus', onSuccess }: PaymentModalProps) {
  const [selectedMethod, setSelectedMethod] = useState<"pix" | "credit">("pix");
  const { subscriptionType, subscriptionStatus, tenantId, refreshTenant } = useTenant();
  const [selectedPlanId, setSelectedPlanId] = useState<string>(planId);
  const [couponCode, setCouponCode] = useState<string>("");
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<{ is_valid: boolean; discounted_amount?: number; must_pay?: boolean; description?: string; image_url?: string; error?: string } | null>(null);
  
  // Buscar dados do plano do banco
  const { data: plan } = useQuery({
    queryKey: ["subscription-plan", selectedPlanId],
    queryFn: async () => {
      if (!selectedPlanId) return null;
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("*")
        .eq("id", selectedPlanId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!selectedPlanId,
  });

  const planPrice = plan?.price || 0;
  const planName = plan?.name || selectedPlanId;
  const canPay = subscriptionType === 'regular' || subscriptionStatus !== 'active';
  const finalAmount = (validation?.is_valid && typeof validation.discounted_amount === 'number') ? (validation.discounted_amount as number) : planPrice;
  const showDiscount = !!(validation?.is_valid && finalAmount !== planPrice);
  const isFree = !!(validation?.is_valid && (finalAmount <= 0 || validation.must_pay === false));

  const handleSuccess = async () => {
    try { await refreshTenant?.(); } catch {}
    onOpenChange(false);
    onSuccess?.();
  };

  const { data: plans } = useQuery({
    queryKey: ["subscription-plans-all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("subscription_plans").select("id, name, price, status").eq("status", "active");
      if (error) throw error;
      return data || [];
    },
  });

  useEffect(() => {
    if (open) {
      setSelectedPlanId(planId);
      setValidation(null);
    }
  }, [planId, open]);

  const handleValidate = async () => {
    setValidating(true);
    setValidation(null);
    try {
      const code = normalizeCouponCode(couponCode);
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      const { data, error } = await supabase.functions.invoke('subscription_coupons', {
        body: { action: 'validate', tenant_id: tenantId, user_id: userId, plan_id: selectedPlanId, code }
      });
      let result = data;
      if (error || !result) {
        const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL || (window as any)?.SUPABASE_URL;
        if (!supabaseUrl) throw new Error('SUPABASE_URL não definido');
        const res = await fetch(`${supabaseUrl}/functions/v1/subscription_coupons`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({ action: 'validate', tenant_id: tenantId, user_id: userId, plan_id: selectedPlanId, code }),
        });
        result = await res.json();
      }
      if (result?.error) {
        const rawError = result.error;
        const errMsg = typeof rawError === 'string' ? rawError : (rawError?.message || (rawError ? JSON.stringify(rawError) : 'Falha na validação do cupom'));
        setValidation({ is_valid: false, error: errMsg });
        return;
      }
      if (!result?.is_valid) {
        const map: Record<string,string> = {
          not_found: 'Cupom não encontrado',
          inactive: 'Cupom inativo',
          before_start: 'Cupom ainda não está válido',
          after_end: 'Cupom expirado',
          plan_not_allowed: 'Cupom não permitido para este plano',
          total_limit_exceeded: 'Limite total de uso do cupom excedido',
          per_user_limit_exceeded: 'Seu limite de uso deste cupom foi excedido',
        };
        let message = map[result?.reason] || 'Cupom inválido';

        // Enriquecer mensagem com detalhes quando possível
        try {
          const { data: couponInfo } = await supabase
            .from('subscription_coupons')
            .select('allowed_plan_ids,start_at,end_at,status')
            .eq('code', code)
            .maybeSingle();
          if (couponInfo) {
            if (result?.reason === 'plan_not_allowed') {
              // Mapear IDs para nomes usando lista de planos carregados
              const byId: Record<string, any> = {};
              (plans || []).forEach((p: any) => { byId[p.id] = p; });
              const allowedNames = (couponInfo.allowed_plan_ids || []).map((pid: string) => byId[pid]?.name).filter(Boolean);
              if (allowedNames.length > 0) {
                message = `Cupom válido apenas para: ${allowedNames.join(', ')}`;
              }
            }
            if (result?.reason === 'before_start' && couponInfo.start_at) {
              message = `Cupom válido a partir de ${new Date(couponInfo.start_at).toLocaleString('pt-BR')}`;
            }
            if (result?.reason === 'after_end' && couponInfo.end_at) {
              message = `Cupom expirado em ${new Date(couponInfo.end_at).toLocaleString('pt-BR')}`;
            }
          }
        } catch {}

        setValidation({ is_valid: false, error: message });
      } else {
        setValidation({ is_valid: true, discounted_amount: result.discounted_amount, must_pay: result.must_pay, description: result.description, image_url: result.image_url });
      }
    } catch (e: any) {
      setValidation({ is_valid: false, error: e?.message || 'Falha na validação' });
    } finally {
      setValidating(false);
    }
  };

  const handleActivateFree = async () => {
    try {
      const code = normalizeCouponCode(couponCode);
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      const { data, error } = await supabase.functions.invoke('subscription_coupons', {
        body: { action: 'apply', tenant_id: tenantId, user_id: userId, plan_id: selectedPlanId, code }
      });
      let result = data;
      if (error || !result) {
        const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL || (window as any)?.SUPABASE_URL;
        if (!supabaseUrl) throw new Error('SUPABASE_URL não definido');
        const res = await fetch(`${supabaseUrl}/functions/v1/subscription_coupons`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({ action: 'apply', tenant_id: tenantId, user_id: userId, plan_id: selectedPlanId, code }),
        });
        result = await res.json();
      }
      if (result?.success || result?.activated_without_payment) {
        handleSuccess();
      } else {
        const rawError = result?.error;
        const errMsg = typeof rawError === 'string' ? rawError : (rawError?.message || (rawError ? JSON.stringify(rawError) : 'Falha ao ativar plano com cupom'));
        throw new Error(errMsg);
      }
    } catch (e: any) {
      const msg = typeof e?.message === 'string' ? e.message : 'Falha na ativação gratuita';
      setValidation({ is_valid: false, error: msg });
    }
  };

  if (!canPay) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Assinar {planName}</DialogTitle>
            <DialogDescription>
              Você possui um plano especial e não pode realizar pagamentos através desta tela.
            </DialogDescription>
          </DialogHeader>
          <Alert>
            <AlertDescription>
              {subscriptionType === 'cortesia' 
                ? 'Você possui um plano de cortesia. Entre em contato para alterar seu plano.'
                : 'Você possui um Plano Beta Tester. Entre em contato para alterar seu plano.'}
            </AlertDescription>
          </Alert>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assinar Plano</DialogTitle>
          <DialogDescription>
            Escolha seu plano e método de pagamento
          </DialogDescription>
        </DialogHeader>

        {/* Seleção de Planos - Botões clicáveis em linha */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Selecione o Plano</label>
          <div className="flex flex-wrap gap-2">
            {plans?.map((p: any) => {
              const isSelected = selectedPlanId === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => {
                    setSelectedPlanId(p.id);
                    setValidation(null); // Limpar validação ao trocar plano
                  }}
                  className={cn(
                    "flex-1 min-w-[150px] px-4 py-3 rounded-lg border-2 transition-all",
                    "hover:border-primary/50",
                    isSelected
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-transparent border-border"
                  )}
                >
                  <div className="text-sm font-semibold">{p.name}</div>
                  <div className="text-xs mt-1">
                    {new Intl.NumberFormat('pt-BR', { 
                      style: 'currency', 
                      currency: 'BRL' 
                    }).format(p.price)}/mês
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Cupom de Desconto - Linha horizontal */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Cupom de Desconto (opcional)</label>
          <div className="flex gap-2">
            <Input
              placeholder="CÓDIGO-DO-CUPOM"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              disabled={validating || validation?.is_valid}
              className="flex-1"
            />
            <Button
              onClick={handleValidate}
              disabled={validating || !couponCode.trim() || validation?.is_valid}
              variant="outline"
              className="min-w-[120px]"
            >
              {validating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Validando
                </>
              ) : validation?.is_valid ? (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Aplicado
                </>
              ) : (
                <>
                  <Tag className="h-4 w-4 mr-2" />
                  Validar
                </>
              )}
            </Button>
          </div>

          {/* Mensagem de erro detalhada */}
          {validation && !validation.is_valid && (
            <Alert variant="destructive">
              <AlertDescription className="text-sm">
                {validation.error || 'Cupom inválido'}
              </AlertDescription>
            </Alert>
          )}

          {/* Cupom válido - Descrição e Imagem */}
          {validation?.is_valid && (
            <div className="p-4 border rounded-lg bg-success/5 border-success/20 space-y-3">
              {/* Imagem do cupom */}
              {validation.image_url && (
                <img 
                  src={validation.image_url} 
                  alt="Cupom" 
                  className="w-full h-32 object-cover rounded-md"
                />
              )}
              
              {/* Descrição do cupom */}
              {validation.description && (
                <p className="text-sm text-foreground">{validation.description}</p>
              )}
              
              {/* Comparação de preços */}
              <div className="flex items-center justify-between pt-2 border-t">
                <div>
                  <span className="text-xs text-muted-foreground">Valor original:</span>
                  <span className="ml-2 line-through text-sm">
                    {new Intl.NumberFormat('pt-BR', { 
                      style: 'currency', 
                      currency: 'BRL' 
                    }).format(planPrice)}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Com cupom:</span>
                  <span className="ml-2 font-bold text-success text-lg">
                    {new Intl.NumberFormat('pt-BR', { 
                      style: 'currency', 
                      currency: 'BRL' 
                    }).format(finalAmount)}
                  </span>
                </div>
              </div>

              {/* Indicador de pagamento isento */}
              {isFree && (
                <div className="text-center p-3 bg-success text-success-foreground rounded-md text-sm font-medium">
                  🎉 Pagamento isento! Plano será ativado gratuitamente.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Botão de ativação gratuita OU métodos de pagamento */}
        {isFree ? (
          <Button 
            onClick={handleActivateFree} 
            className="w-full"
            size="lg"
          >
            Ativar Plano Gratuitamente
          </Button>
        ) : (
          <>
            {/* Mostrar preço final */}
            <div className="text-center py-2">
              <div className="text-sm text-muted-foreground">Total a pagar:</div>
              <div className="text-3xl font-bold">
                {new Intl.NumberFormat('pt-BR', { 
                  style: 'currency', 
                  currency: 'BRL' 
                }).format(finalAmount)}
              </div>
              {showDiscount && (
                <div className="text-xs text-muted-foreground mt-1">
                  Desconto aplicado de{' '}
                  {new Intl.NumberFormat('pt-BR', { 
                    style: 'currency', 
                    currency: 'BRL' 
                  }).format(planPrice - finalAmount)}
                </div>
              )}
            </div>

            {/* Métodos de Pagamento */}
            <Tabs value={selectedMethod} onValueChange={(v) => setSelectedMethod(v as "pix" | "credit")}> 
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="pix" className="gap-2">
                  <QrCode className="h-4 w-4" />
                  PIX
                </TabsTrigger>
                <TabsTrigger value="credit" className="gap-2">
                  <CreditCard className="h-4 w-4" />
                  Cartão de Crédito
                </TabsTrigger>
              </TabsList>
              <TabsContent value="pix" className="mt-4">
                <PixPayment 
                  onSuccess={handleSuccess} 
                  planId={selectedPlanId} 
                  couponCode={couponCode} 
                  displayAmount={finalAmount} 
                />
              </TabsContent>
              <TabsContent value="credit" className="mt-4">
                <CreditCardPayment 
                  onSuccess={handleSuccess} 
                  planId={selectedPlanId} 
                  couponCode={couponCode} 
                  displayAmount={finalAmount} 
                />
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
